import { defineStore } from 'pinia'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { useDataStore } from '@/stores/data'

import type { TourCard, Exhibit } from 'types/data'
import type { ComputedRef, DeepReadonly } from 'vue'

interface ExhibitStoreReturnValue {
  /**
   * Get an exhibit by id.
   * This searches the complete list of `exhibits`.
   *
   * If `locationOnly` is `true` will limit to only hybrid location exhibits.
   */
  getExhibitById: (id: string | number | null, locationOnly?: boolean) => DeepReadonly<Exhibit> | undefined
  /**
   * Get an exhibit by id and number in cases where the id may be duplicated.
   * This searches the complete list of `exhibits`.
   *
   * If `locationOnly` is `true` will limit to only hybrid location exhibits.
   */
  getExhibitByIdAndNumber: (id: string | number | null, number: number, locationOnly?: boolean) => DeepReadonly<Exhibit> | undefined
  /**
   * Indicates whether any Tour or Exhibit has the ability to send souvenirs.
   * In hybrid mode only Exhibits with hybrid support are checked, Tours are always checked.
   */
  hasSouvenirs: ComputedRef<boolean>
  cards: ComputedRef<DeepReadonly<TourCard[]>>
  getCardById: (id: string | number | null) => DeepReadonly<TourCard> | undefined
  /**
   * All exhibits for the current language.
   *
   * Use when not interacting with a location tour (such as regular tours or displays).
   */
  exhibits: ComputedRef<DeepReadonly<Exhibit[]>>
  /**
   * Exhibits that are included in a location tour.
   * This is a subset of `exhibits`.
   *
   * Only use when interacting with a location tour, otherwise use the full list in `exhibits`.
   */
  locationExhibits: ComputedRef<DeepReadonly<Exhibit[]>>
}

export const useExhibitStore = defineStore('exhibit', (): ExhibitStoreReturnValue => {
  const { locale } = useI18n()
  const dataStore = useDataStore()

  const exhibits = computed<DeepReadonly<Exhibit[]>>(() => {
    const languageExhibit = dataStore.languageExhibits.find((l) => l.symbol === locale.value)
    if (!languageExhibit?.exhibits) return []
    return languageExhibit.exhibits
  })

  const locationExhibits: ExhibitStoreReturnValue['locationExhibits'] = computed(() => {
    switch (dataStore.museumType) {
      case 'hybrid': return exhibits.value.filter((e) => e.includeInHybrid)
      default: return exhibits.value
    }
  })

  const hasSouvenirs: ExhibitStoreReturnValue['hasSouvenirs'] = computed(() => {
    return locationExhibits.value.some((e) => {
      if (e.zoneType === 'Exhibit' && dataStore.museumType === 'hybrid' && !e.includeInHybrid) {
        return false
      }
      return e.sendSouvenir
    })
  })

  const getExhibitById: ExhibitStoreReturnValue['getExhibitById'] = (id, locationOnly) => {
    if (!id) return
    const exhibitList = locationOnly ? locationExhibits.value : exhibits.value
    return exhibitList.find((e) => e.id === +id)
  }

  const getExhibitByIdAndNumber: ExhibitStoreReturnValue['getExhibitByIdAndNumber'] = (id, number, locationOnly) => {
    if (!id) return
    const exhibitList = locationOnly ? locationExhibits.value : exhibits.value
    return exhibitList.find((e) => {
      if (e.id !== +id) return false
      if (!number) return true
      return e.number === number
    })
  }

  const cards: ExhibitStoreReturnValue['cards'] = computed(() => {
    const languageTour = dataStore.languageTourCatalogs.find((l) => l.symbol === locale.value)
    if (!languageTour?.cards) return []
    return languageTour.cards
  })

  const getCardById: ExhibitStoreReturnValue['getCardById'] = (id) => {
    if (!id) return
    return cards.value.find((e) => e.tourId === +id)
  }

  return {
    exhibits,
    locationExhibits,
    hasSouvenirs,
    getExhibitById,
    getExhibitByIdAndNumber,
    cards,
    getCardById,
  }
})
